import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import IconButton from "@mui/material/IconButton"
import Badge from "@mui/material/Badge"
import Chip from "@mui/material/Chip"
import CloseIcon from "@mui/icons-material/Close"
import PersonRoundedIcon from "@mui/icons-material/PersonRounded"
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded"
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded"
import { alpha, useTheme } from "@mui/material/styles"
import { blue } from "@mui/material/colors"
import { User } from "../../../interface/User"
import { Transition } from "../../../animations/Transition"

interface UserProfileModalProps
{
  open: boolean
  handleClose: () => void
  user: User
}

export const UserProfileModal = ({ open, handleClose, user }: UserProfileModalProps) =>
{
  const theme = useTheme()

  return(
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: styles.dialog(theme)
      }}
      aria-describedby="user-profile-dialog">
      <DialogTitle sx={styles.titleBar(theme)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box sx={styles.headerIcon(theme)}>
              <PersonRoundedIcon />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight={700}>User Profile</Typography>
            <Typography variant="body2" sx={styles.subtitle(theme)}>
              View your account identity and profile details.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, mt: 1 }}>
        <Stack spacing={1.5} sx={{ width: "100%" }}>
          <Box sx={styles.hero(theme)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md="auto">
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={<Box sx={styles.onlineDot(theme)} />}
                >
                  <Avatar src={user?.userProfileImageUrl} sx={styles.avatar}>
                    {user && user.email ? user.email[0].toUpperCase() : "TB"}
                  </Avatar>
                </Badge>
              </Grid>
              <Grid item xs>
                <Typography variant="overline" sx={styles.eyebrow(theme)}>Account Identity</Typography>
                <Typography variant="h5" fontWeight={700}>
                  {user.username || "Unnamed user"}
                </Typography>
                <Typography variant="body2" sx={styles.subtitle(theme)}>
                  {user.email}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <Chip icon={<AlternateEmailRoundedIcon />} label="Email verified profile" sx={styles.chip(theme)} />
                  <Chip icon={<AccountCircleRoundedIcon />} label={user.username || "No username"} sx={styles.chip(theme)} />
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={styles.fieldCard(theme)}>
            <Typography variant="caption" sx={styles.fieldLabel(theme)}>Email</Typography>
            <TextField
              fullWidth
              placeholder="email"
              variant="outlined"
              value={user.email}
              InputProps={{ readOnly: true }}
              sx={styles.field(theme)}
            />
          </Box>

          <Box sx={styles.fieldCard(theme)}>
            <Typography variant="caption" sx={styles.fieldLabel(theme)}>Username</Typography>
            <TextField
              fullWidth
              placeholder="username"
              variant="outlined"
              value={user.username}
              InputProps={{ readOnly: true }}
              sx={styles.field(theme)}
            />
          </Box>
        </Stack>
      </DialogContent>


    </Dialog>
  )
}

const styles = {
  dialog: (theme: any) => ({
    borderRadius: "18px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  titleBar: (theme: any) => ({
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  hero: (theme: any) => ({
    p: 1.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.68)} 0%, ${alpha("#0c1727", 0.82)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha("#e8f2ff", 0.9)} 100%)`,
    width: "100%",
  }),
  avatar: {
    backgroundColor: blue[500],
    width: 108,
    height: 108,
    fontWeight: "700",
    color: "white",
    fontSize: 34,
  },
  onlineDot: (theme: any) => ({
    width: 14,
    height: 14,
    borderRadius: "999px",
    backgroundColor: theme.palette.success.main,
    border: `2px solid ${theme.palette.background.paper}`,
  }),
  eyebrow: (theme: any) => ({
    display: "block",
    marginTop: 2,
    marginBottom: 2,
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  subtitle: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
  }),
  chip: (theme: any) => ({
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  }),
  fieldCard: (theme: any) => ({
    p: 0.9,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
    width: "100%",
  }),
  fieldLabel: (theme: any) => ({
    display: "block",
    color: alpha(theme.palette.text.primary, 0.62),
    textTransform: "uppercase",
    letterSpacing: 1.1,
  }),
  field: (theme: any) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: "12px",
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.8),
      '& input': {
      },
    },
  }),
  actions: (theme: any) => ({
    px: 3,
    py: 2.25,
    borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02),
  }),
}
